import type React from 'react'
import { useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import styled from 'styled-components'
import { supabase } from '../../lib/supabase'
import { theme } from '../../theme/theme'
import type { CalculatedZone } from '../../types/zones'

interface HeartRateHistogramProps {
  workoutId: string
  minHR?: number // Minimum HR to display on x-axis (default: 100)
  maxHR?: number // Maximum HR to display on x-axis (default: auto-detected from data)
  calculatedZones: CalculatedZone[]
  vt1?: number // Ventilatory Threshold 1 / LTHR
  vt2?: number // Ventilatory Threshold 2 / LTHR2
  workoutDurationMinutes?: number // Total workout duration in minutes for y-axis scaling
  meanHR?: number // Mean heart rate for statistical markers
  stdDevHR?: number // Standard deviation of heart rate
}

interface HistogramBucket {
  bpm: number
  count: number
  percentage: number
  timeInMinutes: number // Time spent at this heart rate in minutes
}

const Container = styled.div`
  width: 100%;

  /* Remove blue outline on chart elements when clicked */
  .recharts-surface {
    outline: none;
  }

  .recharts-wrapper {
    outline: none;
  }
`

const ZoneTotalsContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  justify-content: center;
  margin-top: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  flex-wrap: wrap;

  @media (max-width: 640px) {
    gap: ${theme.spacing.sm};
    padding: ${theme.spacing.sm};
  }
`

const ZoneTotalItem = styled.div<{ $color: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xs};
`

const ZoneTotalLabel = styled.div`
  font-size: ${theme.typography.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 500;
`

const ZoneTotalTime = styled.div<{ $color: string }>`
  font-size: ${theme.typography.md};
  font-weight: 600;
  color: ${({ $color }) => $color};
`

const ZoneRange = styled.div`
  font-size: ${theme.typography.xs};
    color: ${({ theme }) => theme.colors.textMuted};
`

const LoadingMessage = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.sm};
`

const ErrorMessage = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.error};
  font-size: ${theme.typography.sm};
`

const SkeletonWrapper = styled.div`
  .react-loading-skeleton {
    --base-color: ${({ theme }) => theme.colors.surface};
    --highlight-color: ${({ theme }) => theme.colors.border};
    border-radius: ${theme.borderRadius.md};
  }
`

const ChartSkeletonContainer = styled.div`
  width: 100%;
  height: 400px;
  padding: ${theme.spacing.md};
`

// Helper function to format time as hh:mm:ss
const formatTimeHMS = (minutes: number): string => {
  const totalSeconds = Math.round(minutes * 60)
  const hours = Math.floor(totalSeconds / 3600)
  const mins = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60

  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Custom tooltip component
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div
        style={{
          background: theme.colors.cardBackground,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.borderRadius.sm,
          padding: theme.spacing.sm,
          fontSize: theme.typography.xs,
          color: theme.colors.text,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: theme.spacing.xs }}>
          <strong>{data.bpm} bpm</strong>
        </div>
        <div>Time: {formatTimeHMS(data.timeInMinutes)}</div>
      </div>
    )
  }
  return null
}

export const HeartRateHistogram: React.FC<HeartRateHistogramProps> = ({
  workoutId,
  minHR = 100,
  maxHR = 180,
  calculatedZones,
  vt1,
  vt2,
  meanHR,
  stdDevHR,
}) => {
  const [histogramData, setHistogramData] = useState<HistogramBucket[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadHeartRateData = async () => {
      if (!workoutId) return

      try {
        setLoading(true)
        setError(null)

        // Call the database function to get aggregated histogram data
        const { data: histogramData, error: histogramError } = await supabase.rpc('get_heart_rate_histogram', {
          p_workout_id: workoutId,
        })

        if (histogramError) throw histogramError

        if (!histogramData || histogramData.length === 0) {
          setHistogramData([])
          setLoading(false)
          return
        }

        // Calculate total samples for percentage calculation
        const totalSamples = histogramData.reduce((sum, row) => sum + row.sample_count, 0)

        // Convert database results to histogram buckets
        const bucketMap = new Map<number, { count: number; timeSeconds: number }>()
        let dataMinBpm = Infinity
        let dataMaxBpm = -Infinity

        histogramData.forEach((row) => {
          const bpm = row.bpm
          bucketMap.set(bpm, {
            count: row.sample_count,
            timeSeconds: row.time_seconds,
          })
          dataMinBpm = Math.min(dataMinBpm, bpm)
          dataMaxBpm = Math.max(dataMaxBpm, bpm)
        })

        // Determine display range
        const displayMinBpm = minHR
        const displayMaxBpm = maxHR

        // Fill in buckets for the display range (including empty buckets)
        const allBuckets: HistogramBucket[] = []
        for (let bpm = displayMinBpm; bpm <= displayMaxBpm; bpm++) {
          const bucketData = bucketMap.get(bpm)
          const count = bucketData?.count || 0
          const timeInMinutes = bucketData ? bucketData.timeSeconds / 60 : 0
          allBuckets.push({
            bpm,
            count,
            percentage: totalSamples > 0 ? (count / totalSamples) * 100 : 0,
            timeInMinutes,
          })
        }

        setHistogramData(allBuckets)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load heart rate data')
        console.error('Error loading heart rate data:', err)
      } finally {
        setLoading(false)
      }
    }

    loadHeartRateData()
  }, [workoutId, maxHR, minHR])

  const getBarColor = (bpm: number): string => {
    // determine zone based on calculatedZones
    if (!calculatedZones) {
      return '#6B7280' // Gray
    }
    const zone = calculatedZones.find(
      (zone) => (!zone.minValue || bpm >= zone.minValue) && (!zone.maxValue || bpm <= zone.maxValue)
    )
    if (!zone) {
      return '#6B7280' // Gray
    }
    return zone.color
  }

  if (loading) {
    return (
      <Container>
        <ChartSkeletonContainer>
          <SkeletonWrapper>
            <Skeleton height={350} />
          </SkeletonWrapper>
        </ChartSkeletonContainer>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>Error: {error}</ErrorMessage>
      </Container>
    )
  }

  if (histogramData.length === 0) {
    return (
      <Container>
        <LoadingMessage>No heart rate data available for this workout.</LoadingMessage>
      </Container>
    )
  }

  // Calculate time-in-zone totals
  const zoneTotals: Record<string, number> = {}
  if (calculatedZones) {
    calculatedZones.forEach((zone) => {
      zoneTotals[zone.id] = 0
    })

    histogramData.forEach((bucket) => {
      const bpm = bucket.bpm
      const time = bucket.timeInMinutes

      for (const zone of calculatedZones) {
        if ((!zone.minValue || bpm >= zone.minValue) && (!zone.maxValue || bpm < zone.maxValue)) {
          zoneTotals[zone.id] += time
          break
        }
      }
    })
  }

  return (
    <Container>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={histogramData} margin={{ top: 30, right: 30, left: 0, bottom: 5 }}>
          <CartesianGrid stroke="#E5E7EB" />
          <XAxis
            dataKey="bpm"
            stroke={theme.colors.textSecondary}
            tick={{ fill: theme.colors.textSecondary, fontSize: 10 }}
            domain={[minHR, maxHR]}
            type="number"
            ticks={Array.from({ length: Math.floor((maxHR - minHR) / 5) + 1 }, (_, i) => minHR + i * 5)}
          />
          <YAxis
            stroke={theme.colors.textSecondary}
            tick={{ fill: theme.colors.textSecondary, fontSize: 10 }}
            label={{
              value: 'Time',
              angle: -90,
              position: 'insideLeft',
              fill: theme.colors.text,
            }}
            tickFormatter={(value) => formatTimeHMS(value)}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: theme.colors.primaryLight }} />
          <Bar dataKey="timeInMinutes" radius={[2, 2, 0, 0]}>
            {histogramData.map((entry) => (
              <Cell key={`cell-${entry.bpm}`} fill={getBarColor(entry.bpm)} />
            ))}
          </Bar>

          {/* VT1 Reference Line */}
          {vt1 && (
            <ReferenceLine
              x={vt1}
              stroke="#F59E0B"
              strokeWidth={2}
              opacity={0.5}
              strokeDasharray="5 5"
              label={{
                value: `VT1 (${vt1})`,
                position: 'top',
                fill: '#F59E0B',
                fontSize: 12,
                fontWeight: 600,
              }}
            />
          )}

          {/* VT2 Reference Line  */}
          {vt2 && (
            <ReferenceLine
              x={vt2}
              stroke="#EF4444"
              strokeWidth={2}
              opacity={0.5}
              strokeDasharray="5 5"
              label={{
                value: `VT2 (${vt2})`,
                position: 'top',
                fill: '#EF4444',
                fontSize: 12,
                fontWeight: 600,
              }}
            />
          )}

          {/* Mean Heart Rate Reference Line */}
          {meanHR && (
            <ReferenceLine
              x={meanHR}
              stroke="#999999"
              strokeWidth={2}
              label={{
                value: `Mean (${Math.round(meanHR)})`,
                position: 'top',
                fill: '#999999',
                fontSize: 12,
                fontWeight: 500,
              }}
            />
          )}

          {/* Mean - 1 SD Reference Line
          {meanHR && stdDevHR && (
            <ReferenceLine
              x={meanHR - stdDevHR}
              stroke="#AAAAAA"
              strokeWidth={1}
              strokeDasharray="3 3"
              label={{
                value: `-1 SD (${Math.round(meanHR - stdDevHR)})`,
                position: 'top',
                fill: '#AAAAAA',
                fontSize: 11,
                fontWeight: 400,
              }}
            />
          )} */}

          {/* Mean + 1 SD Reference Line
          {meanHR && stdDevHR && (
            <ReferenceLine
              x={meanHR + stdDevHR}
              stroke="#AAAAAA"
              strokeWidth={1}
              strokeDasharray="3 3"
              label={{
                value: `+1 SD (${Math.round(meanHR + stdDevHR)})`,
                position: 'top',
                fill: '#AAAAAA',
                fontSize: 11,
                fontWeight: 400,
              }}
            />
          )} */}
        </BarChart>
      </ResponsiveContainer>

      {/* Zone Totals */}
      <ZoneTotalsContainer>
        {calculatedZones.map((zone) => (
          <ZoneTotalItem $color={zone.color} key={zone.id}>
            <ZoneTotalLabel>{zone.name}</ZoneTotalLabel>
            <ZoneTotalTime $color={zone.color}>{formatTimeHMS(zoneTotals[zone.id])}</ZoneTotalTime>
            <ZoneRange>
              {zone.minValue}-{zone.maxValue}
            </ZoneRange>
          </ZoneTotalItem>
        ))}
      </ZoneTotalsContainer>
    </Container>
  )
}
